
import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage } from 'react-intl';

// Constants

import { configs } from '@constants/configs/configs';
import { UPLOADED_DOCUMENT_STATUSES, UPLOAD_MODAL_VIEWS } from '@constants/common/miscellaneous';
import { checkUserStatus, USER_GROUP_ROLES } from '@constants/roles/userRoleTypes';
import states from '@constants/configs/states';
import { TYPES_EXCEPTIONS_FOR_LABELS } from '@constants/configs/QLEDocumentVerificationConfigs/documentsVerificationConstants';



const DocumentInfo = props => {
    const {
        config,
        documents,
        onClickAction,
        setModalData,
        stateCode,
        status,
        userRole
    } = props;

    const {
        INITIAL,
        REJECTED,
        SUBMITTED,
        VERIFIED
    } = UPLOADED_DOCUMENT_STATUSES;

    const {
        HANDLE_DOCUMENT,
        REMOVE_DOCUMENT
    } = UPLOAD_MODAL_VIEWS;

    const {
        documentStatus,
        uploadedFileName,
        uploadedFileSize
    } = useMemo(
        () => (d => d ? d : {})(documents.find(d => d.documentStatus === INITIAL)),
        [ documents ]
    );

    const documentInfoItemClassName  = useMemo(
        () => {
            return [
                'qle-document-info__item',
                `qle-document-info__item--${ status }`,
                status === INITIAL ? 'pointer qle_uploaded_document--hovered' : ''
            ].join(' ');
        },
        [ status ]
    );

    if(!documents.length) {
        return null;
    }

    const getStatusIcon = document => {

        const {
            documentType,
            encryptedId: encTicketId,
            ticket,
            uploadedFileName,
            uploadedFileSize
        } = document;

        const isException = stateCode === states.NEW_JERSEY && documentType === TYPES_EXCEPTIONS_FOR_LABELS.SPECIAL_CIRCUMSTANCE_ATTESTATION
            ? `${documentType}_SPECIAL_LABEL`
            : null;

        const isSuperUser = checkUserStatus(USER_GROUP_ROLES.SUPERUSERS, userRole);

        const handleTicketLinkClick = e => {

            e.preventDefault();

            const href = configs.endpoints.TICKET_MGMT_TICKET_DETAIL_BACKDOOR
                .processReplacements({ encTicketId });

            setModalData({
                config,
                enhancements: [
                    {
                        path: 'mainLabel',
                        value: 'modals.main_label.uploaded_documents'
                    },
                    {
                        path: 'modalHeader',
                        value: 'modals.header.document_handle'
                    },
                    {
                        path: 'modalInfoItems',
                        value: [
                            {
                                label: <FormattedMessage id = 'modals.upload_document.first_item.header' />,
                                value: <FormattedMessage  id = { `qle.document.${isException ? isException : documentType}` }/>
                            },
                            {
                                label: <FormattedMessage id = 'modals.upload_document.second_item.header' />,
                                value: (
                                    <a href = { href }>{ ticket }</a>
                                )
                            }
                        ]
                    },
                    {
                        path: 'uploadedFileName',
                        value: uploadedFileName
                    },
                    {
                        path: 'uploadedFileSize',
                        value: uploadedFileSize
                    },
                    {
                        path: 'view',
                        value: HANDLE_DOCUMENT
                    }
                ]
            });
        };

        if(isSuperUser && status === SUBMITTED) {
            return (
                <a
                    href = '#'
                    id = { `ticket-redirection-link_${ticket}` }
                    onClick = { handleTicketLinkClick }
                >
                    { ticket }
                </a>
            );
        }

        let icon;
        switch(status) {
            case INITIAL:
                icon = 'edit';
                break;
            case REJECTED:
                icon = 'times-circle';
                break;
            case SUBMITTED:
                icon = 'check';
                break;
            case VERIFIED:
                icon = 'check-circle';
                break;
        }

        return <FontAwesomeIcon icon = { icon }/>;
    };

    const getStatusLabelTextId = () => {
        switch(status) {
            case INITIAL:
                return 'common.no_text';
            case SUBMITTED:
                return 'qle.common.submitted';
            case REJECTED:
                return 'qle.common.rejected';
            case VERIFIED:
                return 'qle.common.approved';
        }
    };

    const handleRemovalClick = e => {
        if(documentStatus === INITIAL) {
            onClickAction(e, {
                view: REMOVE_DOCUMENT,
                uploadedFileName,
                uploadedFileSize
            });
        }
    };

    const handleViewClick = async e => {
        const {
            currentTarget: {
                dataset: {
                    encrypted_path: encryptedDocumentPath
                }
            }
        } = e;

        e.preventDefault();
        e.stopPropagation();

        const src = configs.ENV.DEVELOPMENT && encryptedDocumentPath.includes('blob:http://localhost:8888')
            ? encryptedDocumentPath
            : configs.endpoints.UPLOADED_DOCUMENT_URL.processReplacements({ encryptedDocumentPath });

        // ViewImageModal call was replaced by browser built-in popup per Aamir's request

        const height = 600;
        const width = 1200;
        const left   = (screen.width  - width)/2;
        const top = (screen.height - height)/2;

        const combineParams = windowParams => Object
            .entries(windowParams)
            .reduce(
                (result, [ key, value ]) => {
                    return result + key + '=' + value + ',';
                },
                ''
            );

        window.open(src, '_blank', combineParams({ width, height, left, top }));
    };

    const getDocuments = () => {
        return documents.map((document, idx) => {

            const {
                encryptedDocumentPath,
                showDocument = true,
                uploadedFileName
            } = document;

            return (
                <div className = 'grid-row' key = { `${uploadedFileName}_${idx}` }>
                    <div className = 'mobile:grid-col-12 tablet:grid-col-8 desktop:grid-col-9'>
                        <span
                            id = 'qle-document-info-common-icon'
                            className = 'qle-document-info__item__icon--common'
                        >
                            <FontAwesomeIcon icon = 'file'/>
                        </span>
                        <span id = 'qle-document-info-file-name'>
                            { uploadedFileName }
                        </span>
                    </div>
                    <div id = 'qle-document-info-status-wrapper' className = 'mobile:grid-col-12 tablet:grid-col-4 desktop:grid-col-3'>
                        <div className= 'grid-row'>
                            {
                                showDocument
                                    ? (
                                        <div
                                            className = 'grid-col qle-document-info__item__icon text-center cursor-pointer'
                                            onClick = { handleViewClick }
                                            data-encrypted_path = { encryptedDocumentPath }
                                            data-file_name = { uploadedFileName }
                                        >
                                            <FontAwesomeIcon icon = 'eye' />
                                        </div>
                                    )
                                    : null
                            }
                            <div id = 'qle-document-info-status-icon' className = 'grid-col qle-document-info__item__icon--status text-center'>
                                { getStatusIcon(document) }
                            </div>
                            <div id = 'qle-document-info-status-label' className= 'grid-col text-center'>
                                <FormattedMessage id = { getStatusLabelTextId() }/>
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };



    return (
        <div
            className = { documentInfoItemClassName }
            id = 'qle-document-info-item'
            onClick = { handleRemovalClick }
        >
            { getDocuments() }
        </div>
    );
};

export default DocumentInfo;